import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { apiGet } from '../composables/apiGet'
import { apiMutate } from '../composables/apiMutate'
import { useAsyncResource } from '../composables/useAsyncResource'
import { useChannelSubscription } from '../composables/useChannelSubscription'
import { useCvVersionsStore } from './cv_versions'

// Admin-only store for CV generation runs. `POST /cv_versions` answers 202
// with a run (or the run already in flight); the job's progress arrives
// over CvGenerationRunChannel, so the page never polls. A succeeded run
// refetches the versions list and selects the draft it produced.

export type CvGenerationRunStatus = 'queued' | 'running' | 'succeeded' | 'failed'

export interface CvGenerationRun {
  id: number
  status: CvGenerationRunStatus
  started_at: string | null
  finished_at: string | null
  error: string | null
  model: string | null
  input_tokens: number | null
  output_tokens: number | null
  cache_read_tokens: number | null
  cache_creation_tokens: number | null
  cv_version_id: number | null
  created_at: string
}

// Broadcast payload from the model's Broadcastable declaration.
export interface CvGenerationRunEvent {
  event: 'created' | 'updated' | 'destroyed'
  record: CvGenerationRun
}

// Failed runs shown next to the versions list; older ones stay in the API.
const FAILED_SHOWN = 3

export function isActiveRun(run: CvGenerationRun): boolean {
  return run.status === 'queued' || run.status === 'running'
}

export const useCvGenerationRunsStore = defineStore('cvGenerationRuns', () => {
  const versions = useCvVersionsStore()

  const {
    data: runs,
    loaded,
    loading,
    error: loadError,
    refresh: fetchRuns,
  } = useAsyncResource<CvGenerationRun[]>(
    async () => {
      const body = await apiGet<{ runs: CvGenerationRun[] }>('/api/v1/cv_generation_runs', {
        failureLabel: 'Failed to load CV generation runs',
      })
      return body?.runs ?? []
    },
    { initialValue: [] },
  )

  const active = computed(() => runs.value.find(isActiveRun) ?? null)
  const failed = computed(() => runs.value.filter((r) => r.status === 'failed').slice(0, FAILED_SHOWN))
  const triggering = ref(false)

  // Newest first, matching the API: an unseen run goes to the front.
  function upsert(run: CvGenerationRun): void {
    const known = runs.value.some((r) => r.id === run.id)
    runs.value = known ? runs.value.map((r) => (r.id === run.id ? run : r)) : [run, ...runs.value]
  }

  async function generate(): Promise<CvGenerationRun> {
    triggering.value = true
    try {
      const run = await apiMutate<CvGenerationRun>('/api/v1/cv_versions', {
        method: 'POST',
        failureLabel: 'failed to queue a CV draft',
      })
      upsert(run)
      return run
    } finally {
      triggering.value = false
    }
  }

  async function adoptDraft(run: CvGenerationRun): Promise<void> {
    await versions.fetchVersions()
    if (run.cv_version_id != null) versions.select(run.cv_version_id)
  }

  function applyEvent(data: CvGenerationRunEvent): void {
    if (data.event === 'destroyed') {
      runs.value = runs.value.filter((r) => r.id !== data.record.id)
      return
    }
    const before = runs.value.find((r) => r.id === data.record.id)
    upsert(data.record)
    if (data.record.status === 'succeeded' && before?.status !== 'succeeded') void adoptDraft(data.record)
  }

  function subscribe(): void {
    useChannelSubscription<CvGenerationRunEvent>('CvGenerationRunChannel', {
      received: applyEvent,
      // A run that finished during a WS drop would otherwise strand the
      // spinner and hide its draft.
      refetchOnReconnect() {
        void fetchRuns()
        void versions.fetchVersions()
      },
    })
  }

  return { runs, loaded, loading, loadError, active, failed, triggering, fetchRuns, generate, applyEvent, subscribe }
})
